import Content from '../constants/Content';

/**
 * Gets the productionized url for the given path.
 *
 * @param pathname path components such as /product or /terms-of-service. Default path is "/" and refers to base url.
 */
const getCanonicalUrl = (pathname = '/') => {
  let newPath = '';

  if (!pathname || pathname === '/' || pathname.toLowerCase() === '/index.html') {
    newPath = Content.names.mainUrl;
  } else {
    let normalizedPathname = pathname;

    const shopPageMatch = normalizedPathname.match(
      /^\/shop\/([^/]+)\/(\d+)(?:\/[^/]+)?\/?$/,
    );
    if (shopPageMatch) {
      const category = shopPageMatch[1];
      const pageNum = Number(shopPageMatch[2]);
      if (pageNum === 1) {
        normalizedPathname = `/shop/${category}`;
      } else {
        normalizedPathname = `/shop/${category}/${pageNum}`;
      }
    }

    // URL encode special characters, but leave path slashes alone. Generally
    // worried about paths that contain an & from product names.
    newPath =
      Content.names.mainUrl +
      encodeURIComponent(decodeURIComponent(normalizedPathname)).replace(
        /%2F/g,
        '/',
      );
    // The old products pages were /product/ (singular) but the new site is
    // /products/ (plural). If a bot re-crawls the old version, indicate the
    // new canonical version as plural.
    newPath = newPath.replace('/product/', '/products/');
    if (newPath.endsWith('/')) {
      newPath = newPath.slice(0, -1);
    }
  }
  return newPath.toLowerCase();
};

/**
 * Gets a properly formatted title.
 *
 * @param title The portion of the title that will be added after the prefix.
 */
const getTemplatedTitle = (title) => {
  if (!title) {
    return Content.meta.defaultTitlePrefix + Content.meta.defaultTitleSuffix;
  }
  return title + Content.meta.defaultTitleSuffix;
};

const getTemplatedDescription = (description) => {
  if (!description) {
    return (
      Content.meta.defaultDescriptionPrefix +
      Content.meta.defaultDescriptionSuffix
    );
  }
  return description + Content.meta.defaultDescriptionSuffix;
};

const getTrimmedDescription = (description, maxLength = 152) => {
  let metaDescription = '';
  if (description && description.trim().length) {
    metaDescription = description
      .replace(/(<([^>]+)>)/gi, '')
      .substring(0, maxLength);

    metaDescription += '...';
  }
  return metaDescription;
};

/**
 * Meta description for a product page: the curated metaDescription if the
 * product has one, else its description (HTML-stripped + trimmed), else the
 * site default. Prevents serving an empty description tag (Semrush #106).
 *
 * Variant products (e.g. tumbler colors) share one DB `description`, so the
 * raw fallback would be identical across their PDPs and trip Semrush's
 * duplicate-meta-description check (#15). To keep the fallback unique per
 * product, the product name is prefixed (`"<name> - <description>"`) when one
 * exists and the description does not already lead with it. The description
 * portion is trimmed so the combined string stays within meta-description
 * length bounds. Curated metaDescription always wins, returned as-is.
 */
const getProductMetaDescription = (productInfo = {}) => {
  if (productInfo.metaDescription?.trim()) {
    return productInfo.metaDescription;
  }

  const name = productInfo.name?.trim();
  if (name) {
    const plainDescription = productInfo.description
      ?.replace(/(<([^>]+)>)/gi, '')
      .trim();
    if (
      plainDescription &&
      !plainDescription.toLowerCase().startsWith(name.toLowerCase())
    ) {
      const prefix = `${name} - `;
      // Budget the description portion so prefix + description + '...' stays
      // within a normal meta-description length (~170 chars max).
      const descriptionBudget = Math.max(170 - prefix.length - 3, 0);
      return prefix + getTrimmedDescription(plainDescription, descriptionBudget);
    }
  }

  return (
    getTrimmedDescription(productInfo.description) || getTemplatedDescription()
  );
};

// Semrush flags a <title> longer than 70 chars (#102). The global react-helmet
// titleTemplate appends `defaultTitleSuffix` (' | Bump Boxes') to every title,
// so the product title text must leave room for the suffix.
const PRODUCT_TITLE_MAX_LENGTH = 70;

/**
 * Title text for a product page's <title> (the suffix is added by the global
 * titleTemplate). A curated `metaTitle` wins when present; otherwise it is built
 * from `brand + name` and, when that would push the rendered title past 70
 * chars, trimmed on a word boundary so the rendered <title> stays within bounds
 * (#102) while keeping enough of the name to stay unique across products (#6).
 *
 * Note: the product GraphQL query does not yet select `metaTitle`, so the
 * curated branch is forward-compatible (inert in production until `metaTitle`
 * is added to the product fragment + backend). Today titles are algorithmic.
 */
const getProductTitle = (productInfo = {}) => {
  const curated = productInfo.metaTitle?.trim();
  if (curated) {
    return curated;
  }

  const name = (productInfo.name || '').trim();
  const brand = (productInfo.brand || '').trim();
  const base = (brand ? `${brand} ${name}` : name).trim();

  const maxBase =
    PRODUCT_TITLE_MAX_LENGTH - Content.meta.defaultTitleSuffix.length;
  if (base.length <= maxBase) {
    return base;
  }

  const truncated = base.slice(0, maxBase);
  const lastSpace = truncated.lastIndexOf(' ');
  return (lastSpace > 0 ? truncated.slice(0, lastSpace) : truncated).trimEnd();
};

const getProductSku = (product) => {
  if (product.subscriptionPlan) {
    return product.subscriptionPlan.sku;
  }
  return product.sku;
};

export default {
  getCanonicalUrl,
  getTemplatedTitle,
  getTemplatedDescription,
  getTrimmedDescription,
  getProductMetaDescription,
  getProductTitle,
  getProductSku,
};
